import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { DragDropContext, Draggable, DropResult } from 'react-beautiful-dnd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare, faPlus } from '@fortawesome/free-solid-svg-icons';
import { useModal } from 'components/modals/ModalContext';
import { useGlobalContext } from 'GlobalContext';
import { useFetchData } from 'services/api/useFetchData';
import { saveData } from 'services/api/saveData';
import { StrictModeDroppable } from 'services/utils/dragDropUtils';
import PrimaryButton from 'components/buttons/PrimaryButton';
import SecondaryButton from 'components/buttons/SecondaryButton';
import DeleteModal from 'components/modals/DeleteModal';
import KnowledgeBaseGroupForm from './KnowledgeBaseGroupForm';
import KnowledgeBaseGroupAdminPage from './KnowledgeBaseGroupAdminPage';
import { KnowledgeBaseGroupCard } from '../../knowledge_base/KnowledgeBaseCards';
import { KnowledgeBaseGroup, KNOWLEDGE_BASE_ADMIN_PATH, KNOWLEDGE_BASE_PUBLIC_PATH } from '../../knowledge_base/KnowledgeBaseTypes';
import '../../../style/scss/knowledge-base.scss';

// The management page of the knowledge base showing the group overview or a single group
const KnowledgeBaseAdminPage: React.FC = () => {
    const groupMatch = useRouteMatch(`${KNOWLEDGE_BASE_ADMIN_PATH}/group/:id`);
    return groupMatch ? <KnowledgeBaseGroupAdminPage /> : <KnowledgeBaseGroupOverview />;
};

// The management overview of the knowledge base, showing the groups as in the knowledge base itself
const KnowledgeBaseGroupOverview: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { initializeModal } = useModal();
    const { setFloatingAlert } = useGlobalContext();
    const [groups, setGroups] = useState<KnowledgeBaseGroup[]>([]);

    // Fetch the groups of the knowledge base
    const { response, loading, refetch } = useFetchData({ apiUrl: 'get_knowledge_base_group_list_as_superuser' });

    // Store the fetched groups in the local state for drag and drop
    useEffect(() => {
        if (response?.results) setGroups(response.results);
    }, [response]);

    // Opens the form modal to add a group
    const handleAddGroup = () => {
        initializeModal(
            <KnowledgeBaseGroupForm />,
            { title: t('knowledge_base.group.add_title'), modalSize: 'small', onSuccess: () => refetch() }
        );
    };

    // Opens the form modal to edit a group
    const handleEditGroup = (group: KnowledgeBaseGroup) => {
        initializeModal(
            <KnowledgeBaseGroupForm />,
            { title: t('knowledge_base.group.edit_title'), modalSize: 'small', itemId: group.id, onSuccess: () => refetch() }
        );
    };

    // Opens the delete confirmation of a group
    const handleDeleteGroup = (group: KnowledgeBaseGroup) => {
        initializeModal(
            <DeleteModal onConfirmDelete={async () => {
                try {
                    await saveData({ apiUrl: `patch_knowledge_base_group_as_superuser/${group.id}`, method: 'patch', data: { deleted: true } });
                    refetch();
                } catch (error) {
                    setFloatingAlert({ type: 'danger', message: 'knowledge_base.general.save_failed_message' });
                }
            }} />,
            { modalSize: 'extra-small' }
        );
    };

    // Stores the new ordering after a drag
    const handleDragEnd = async (result: DropResult) => {
        if (!result.destination || result.destination.index === result.source.index) return;

        // Reorder the groups locally
        const reordered = Array.from(groups);
        const [moved] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, moved);
        const updated = reordered.map((group, index) => ({ ...group, ordering: index }));
        setGroups(updated);

        // Store the ordering in the backend
        try {
            await saveData({
                apiUrl: 'patch_knowledge_base_group_ordering_as_superuser',
                method: 'patch',
                data: updated.map(group => ({ id: group.id, ordering: group.ordering })),
            });
        } catch (error) {
            setFloatingAlert({ type: 'danger', message: 'knowledge_base.general.save_failed_message' });
            refetch();
        }
    };

    return (
        <div className='container-filled kb-admin-page'>
            <div className='kb-admin-toolbar'>
                <p className='kb-admin-intro'>{t('knowledge_base.general.admin_intro')}</p>
                <div className='kb-admin-toolbar-buttons'>
                    <SecondaryButton
                        label='knowledge_base.general.open_public_button'
                        size='small'
                        icon={<FontAwesomeIcon icon={faArrowUpRightFromSquare} />}
                        onClick={() => window.open(KNOWLEDGE_BASE_PUBLIC_PATH, '_blank')} />
                    <PrimaryButton
                        label='knowledge_base.group.add_button'
                        size='small'
                        icon={<FontAwesomeIcon icon={faPlus} />}
                        onClick={handleAddGroup} />
                </div>
            </div>

            {loading === 'success' && groups.length === 0 && (
                <div className='kb-empty-state'>{t('knowledge_base.group.empty_state')}</div>
            )}

            <DragDropContext onDragEnd={handleDragEnd}>
                <StrictModeDroppable droppableId='kb-groups'>
                    {(provided) => (
                        <div className='kb-card-list' ref={provided.innerRef} {...provided.droppableProps}>
                            {groups.map((group, index) => (
                                <Draggable key={group.id} draggableId={`group-${group.id}`} index={index} isDragDisabled={groups.length <= 1}>
                                    {(dragProvided) => (
                                        <div ref={dragProvided.innerRef} {...dragProvided.draggableProps}>
                                            <KnowledgeBaseGroupCard
                                                group={group}
                                                onClick={() => history.push(`${KNOWLEDGE_BASE_ADMIN_PATH}/group/${group.id}`)}
                                                adminActions={{
                                                    onEdit: () => handleEditGroup(group),
                                                    onDelete: () => handleDeleteGroup(group),
                                                    dragHandleProps: dragProvided.dragHandleProps ?? undefined,
                                                }} />
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </StrictModeDroppable>
            </DragDropContext>
        </div>
    );
};

export default KnowledgeBaseAdminPage;
